import * as React from "react"
import { SyntheticEvent } from "react"
import { observable } from "mobx"
import { RouteComponentProps } from "react-router"
import { observer } from "mobx-react"
import FormState from "../common/FormState"
import ApiClient from "../api/ApiClient"
import ErrorBag from "../common/ErrorBag"
import Util, { logException, publicPath } from "../common/Util"
import classNames from "classnames"
import AppStateStore from "../stores/AppStateStore"
import ButtonLoader from "../components/ButtonLoader"
import { route } from "../routes/routes"
import { Routes } from "../routes/AppRoutes"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"

type RequestPasswordResetFormProps = {
  username: string
}

@observer
export default class RequestPasswordResetView extends React.Component<RouteComponentProps> {
  @observable
  private formData = new FormState<RequestPasswordResetFormProps>({
    username: ''
  })

  @observable
  private submitting = false
  @observable
  private formErrors = new ErrorBag()

  componentDidMount (): void {
    document.body.classList.add('request-password-reset')
  }

  componentWillUnmount (): void {
    document.body.classList.remove('request-password-reset')
  }

  public render () {
    return (
      <div>
        <div className="login-container">
          <div className="logo">
            <img src={publicPath('img/logo-login.png')} alt="LeTip Wired"/>
          </div>
          <div className="login-box">
            <div className="login-header">
              <h2>Reset Your Password</h2>
            </div>
            <div className="login-body">
              <p className="text-center">No worries. Fill out the form below and we'll email you instructions for resetting your password.</p>
              <form method="post" action="" acceptCharset="UTF-8" onSubmit={this.submit}>
                {renderInput('username', 'text', 'Username', 'la-user', this.formData, this.formErrors)}
                <ButtonLoader type="submit" color="primary" block={true} loading={this.submitting}>Send Password Reset</ButtonLoader>
                <div className="text-center login-action" style={{ marginTop: 20 }}>
                  <Link to={route(Routes.login)}>Return to login</Link>
                </div>
              </form>
            </div>
          </div>
          <div className="letip-logo text-center">
            <img src={publicPath('img/letip-logo.png')} alt="LeTip"/>
          </div>
          <div className="login-footer text-center">&copy; {new Date().getFullYear()} LeTip International, Inc. All
            Rights Reserved
            <br />
            <a target="_blank" href="https://letip.com/terms-of-use/">Terms of Use</a> | <a target="_blank" href="https://letip.com/privacy-policy/">Privacy Policy</a>
          </div>
        </div>
      </div>
    )
  }

  private submit = (ev: SyntheticEvent) => {
    ev.preventDefault()

    if (this.submitting) {
      return
    }

    this.formErrors.clearErrors()
    AppStateStore.showModalSpinner()
    this.submitting = true

    ApiClient.resetPasswordRequest(this.formData.get('username'))
      .then(response => {
        toast.success('Password reset request has been sent')
        this.props.history.replace(route(Routes.login))
      }, error => {
        const errors = new ErrorBag()
        Util.handleErrorResponse(error.response, errors, response => {
          if (response.status === 401) {
            errors.addError('username', response.data.message || 'The username you have entered is invalid')
            return true
          }

          return false
        }, (response, message) => {
          errors.addError('username', message)
          return true
        })

        this.formErrors = errors
      })
      .catch(ex => {
        logException(ex)
        this.formErrors = new ErrorBag().addError('username', 'A server error has occurred')
      })
      .then(() => {
        AppStateStore.dismissModalSpinner()
        this.submitting = false
      })
  }
}

function renderInput (name: keyof RequestPasswordResetFormProps, type: string, placeholder: string, icon: string, formState: FormState<RequestPasswordResetFormProps>, formErrors: ErrorBag) {
  return (
    <div className="form-group">
      <div className="input-group">
        <div className="input-group-prepend">
          <span className="input-group-text">
            <i className={classNames('la', icon)}/>
          </span>
        </div>
        <input type={type}
               className={classNames('form-control', { 'is-invalid': formErrors.hasErrors(name) })}
               placeholder={placeholder}
               name={name}
               value={formState.get(name)}
               onChange={formState.onChange}
        />
        {formErrors.hasErrors(name) && <div className="invalid-feedback">{formErrors.getError(name)}</div>}
      </div>
    </div>
  )
}
